import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { CreditCard, ExternalLink, Settings } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import StripeService, { type SubscriptionStatus } from '../services/stripeService'
import styles from './Dashboard.module.css'

export default function Dashboard() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    StripeService.getSubscriptionStatus()
      .then(setSubscription)
      .catch(() => setSubscription(null))
      .finally(() => setLoading(false))
  }, [])

  const handleManageBilling = async () => {
    try {
      const { url } = await StripeService.getCustomerPortalUrl()
      window.open(url, '_blank')
    } catch (err) {
      console.error('Failed to open billing portal:', err)
    }
  }

  return (
    <div className={styles.page}>
      <SEO title="Dashboard | Carbon13" description="Manage your Carbon13 account and subscription." noindex />

      <section className={styles.section}>
        <div className={styles.container}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1>{t('dashboard.welcome')}, {user?.displayName || 'there'}</h1>
            <p className={styles.subtitle}>{t('dashboard.subtitle')}</p>
          </motion.div>

          <div className={styles.grid}>
            <motion.div
              className={styles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className={styles.cardIcon}><CreditCard size={22} /></div>
              <h3>{t('dashboard.subscription')}</h3>
              {loading ? (
                <p className={styles.loading}>Loading...</p>
              ) : subscription ? (
                <>
                  <div className={styles.tierBadge}>{subscription.tierName}</div>
                  <p className={styles.status}>
                    Status: <strong>{subscription.subscriptionStatus || 'Free'}</strong>
                  </p>
                </>
              ) : (
                <p className={styles.status}>No active subscription</p>
              )}
              <Button variant="outline" size="sm" onClick={handleManageBilling}>
                {t('dashboard.manageBilling')} <ExternalLink size={14} />
              </Button>
            </motion.div>

            <motion.div
              className={styles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className={styles.cardIcon}><Settings size={22} /></div>
              <h3>{t('dashboard.account')}</h3>
              <div className={styles.accountDetail}>
                <span className={styles.accountLabel}>Email</span>
                <span className={styles.accountValue}>{user?.email}</span>
              </div>
              <div className={styles.accountDetail}>
                <span className={styles.accountLabel}>Name</span>
                <span className={styles.accountValue}>{user?.displayName || 'Not set'}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
